import { css, keyframes } from '@emotion/react';
import type { StyledComponent } from '@emotion/styled';
import styled from '@emotion/styled';
import type { AccordionProps } from '@hotjar/hotsauce';
import { tokens, Text, Accordion as RawAccordion } from '@hotjar/hotsauce';

interface CompoundedAccordion extends StyledComponent<AccordionProps> {
  Section: typeof RawAccordion.Section;
}

const FadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

export const Accordion = styled(RawAccordion)`
  [data-reach-accordion-item] {
    border: none;

    &:last-of-type {
      border: none;
    }
  }

  [data-reach-accordion-panel] {
    opacity: 1;
    animation: ${FadeIn} 0.5s ease 1;
  }
` as CompoundedAccordion;

const Section = styled(RawAccordion.Section)`
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
  margin: ${tokens.spacer.x1} 0 0;
  border-radius: ${tokens.radius.m};

  svg:first-of-type {
    margin-right: ${tokens.spacer.x2};
    fill: ${tokens.color.text_light};
  }

  &[aria-expanded='true'] svg:first-of-type {
    fill: ${tokens.color.primary_cta};
  }

  ${({ status }) =>
    status === 1
      ? `
    &[aria-expanded] svg:first-of-type {
     fill: ${tokens.color.text_success};
    }
  `
      : ''}

  &:hover {
    box-shadow: none;
    background: rgba(121, 125, 141, 0.09);
  }

  &:focus {
    outline: none;
    ${tokens.focus()};
  }
`;

const Title = styled(Text)<{ status?: 0 | 1 }>`
  ${({ status }) =>
    status === 1
      ? `
    text-decoration: line-through;
    color: ${tokens.color.text_light};
  `
      : ''}
`;

Accordion.Section = ({ icon, title, status, children }) => (
  <Section
    icon={status === 1 ? 'check_alt' : icon}
    iconSize={16}
    status={status}
    title={
      <Title status={status} textType="subtitle2">
        {title}
      </Title>
    }
    headerCustomCss={css`
      padding-left: ${tokens.spacer.x5};
    `}
  >
    {children}
  </Section>
);
